import React, { useState, useEffect } from 'react';
import { recordFuelSale, getAllFuelRates } from '../../services/api';

const FuelEntryPage = ({ user }) => {
  const [fuelForm, setFuelForm] = useState({ fuel_type: 'Petrol', quantity: '', price: '' });
  const [fuelRates, setFuelRates] = useState({ Petrol: 0, Diesel: 0, Power: 0, Oil: 0, Additive: 0 });
  const [message, setMessage] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const ratesResp = await getAllFuelRates();
        setFuelRates(ratesResp.data || {});
        const defaultRate = (ratesResp.data && ratesResp.data.Petrol) || 0;
        // leave price blank for manual entry, but show current per-liter rate as hint
        setFuelForm(prev => ({ ...prev, price: '' }));
      } catch (err) {
        console.warn('Failed to load fuel rates', err);
      }
    };
    load();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const raw = value === '' ? '' : parseFloat(value);

    setFuelForm(prev => {
      const next = { ...prev, [name]: name === 'quantity' || name === 'price' ? raw : value };

      // If user changed price, auto-calculate quantity using current per-liter rate
      if (name === 'price') {
        const ratePerL = parseFloat(fuelRates[next.fuel_type] || 0);
        if (ratePerL > 0 && raw !== '') {
          const qty = parseFloat((raw / ratePerL).toFixed(2));
          next.quantity = qty;
        } else {
          next.quantity = '';
        }
      }

      // If user changed quantity, mirror price using current per-liter rate
      if (name === 'quantity') {
        const ratePerL = parseFloat(fuelRates[next.fuel_type] || 0);
        if (ratePerL > 0 && raw !== '') {
          const total = parseFloat((raw * ratePerL).toFixed(2));
          next.price = total;
        } else {
          next.price = '';
        }
      }

      // If fuel type changes and price exists, recalculate quantity
      if (name === 'fuel_type' && prev && prev.price !== '' && prev.price !== undefined) {
        const ratePerL = parseFloat(fuelRates[next.fuel_type] || 0);
        const priceVal = prev.price;
        if (ratePerL > 0 && priceVal !== '') {
          next.quantity = parseFloat((priceVal / ratePerL).toFixed(2));
        }
      }

      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { fuel_type, quantity, price } = fuelForm;
    
    // Validate inputs
    if (!fuel_type || quantity === '' || price === '') {
      setMessage('Please fill in all fields');
      return;
    }

    // Convert to numbers and validate
    const qty = parseFloat(quantity);
    const totalAmount = parseFloat(price);
    
    if (isNaN(qty) || isNaN(totalAmount)) {
      setMessage('Please enter valid numbers for quantity and price');
      return;
    }
    
    if (qty <= 0 || totalAmount <= 0) {
      setMessage('Quantity and price must be greater than zero');
      return;
    }

    const rate = qty > 0 ? parseFloat((totalAmount / qty).toFixed(2)) : 0;

    // Fix the payload to match what the backend expects
    const payload = {
      fuel_type,
      quantity: qty,
      rate: rate
    };

    try {
      await recordFuelSale(payload);
      setMessage('Fuel sale recorded successfully');
      // Reset only quantity and price, keep the selected fuel type
      setFuelForm(prev => ({ ...prev, quantity: '', price: '' }));
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      console.error('Error recording fuel sale:', err);
      console.error('Error response:', err.response);
      const errorMessage = err.response?.data?.message || err.message || 'Error recording fuel sale';
      setMessage(`Error: ${errorMessage}`);
    }
  };

  return (
    <div className="max-w-3xl mx-auto py-6 px-4">
      <h1 className="text-2xl font-bold mb-4">Record Fuel Sale</h1>
      {message && <div className="mb-4 text-sm text-green-700 bg-green-50 p-2 rounded">{message}</div>}
      <div className="bg-white shadow rounded-md p-4">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-700">Fuel</label>
            <select name="fuel_type" value={fuelForm.fuel_type} onChange={handleInputChange} className="mt-1 block w-full border border-gray-300 rounded-md p-2">
              <option value="Petrol">Petrol</option>
              <option value="Diesel">Diesel</option>
              <option value="Power">Power</option>
              <option value="Oil">Oil</option>
              <option value="Additive">Additive</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700">Quantity (L)</label>
            <input name="quantity" type="number" step="0.01" min="0" value={fuelForm.quantity} onChange={handleInputChange} className="mt-1 block w-full border border-gray-300 rounded-md p-2" />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700">Price (₹)</label>
            <input name="price" type="number" step="0.01" min="0" value={fuelForm.price} onChange={handleInputChange} className="mt-1 block w-full border border-gray-300 rounded-md p-2" />
            <div className="text-xs text-gray-500 mt-1">Enter the total price for the sale</div>
          </div>

          <div>
            <button type="submit" className="w-full bg-red-600 text-white py-2 rounded-md">Record Sale</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FuelEntryPage;
